import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import cartService from '../../services/cartService';
import { syncWeekPlanToCalendar } from '../../services/mealPlanIntegration';
import { event as gaEvent } from '../../utils/analytics';
import { getClientNutritionPlanExitPath } from '../../utils/affiliateNavigation';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { formatConvertedAmount, fromGrams } from '../../utils/weightUnits';
import './Step4Review.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snacks'];

// Transform a single meal for saving to database
const transformMealForSave = (meal) => {
  return {
    foods: (meal.foods || []).map(item => ({
      fdcId: item.food?.fdcId,
      description: item.food?.description,
      category: item.food?.category,
      brandOwner: item.food?.brandOwner,
      nutrients: item.food?.nutrients,
      quantity: item.quantity,
      servingSize: item.servingSize
    })),
    menuItems: (meal.menuItems || []).map(item => ({
      menuItemId: item.menuItem?._id,
      title: item.menuItem?.title,
      image: item.menuItem?.image,
      nutrition: item.menuItem?.nutrition,
      servings: item.servings
    }))
  };
};

// Transform week plan for database storage
const transformWeekPlanForSave = (weekPlan) => {
  return weekPlan.map(day => ({
    dayNumber: day.dayNumber,
    date: day.date,
    dayName: day.dayName,
    meals: {
      breakfast: transformMealForSave(day.meals.breakfast),
      lunch: transformMealForSave(day.meals.lunch),
      dinner: transformMealForSave(day.meals.dinner),
      snacks: transformMealForSave(day.meals.snacks)
    }
  }));
};
const MEAL_ICONS = {
  breakfast: 'fa-sun',
  lunch: 'fa-cloud-sun',
  dinner: 'fa-moon',
  snacks: 'fa-cookie-bite'
};

const Step4Review = ({ wizardData, cart, currentGoalId }) => {
  const weightUnit = useWeightUnit();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [addingToCart, setAddingToCart] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [clientName, setClientName] = useState(null);
  const [copyWeeks, setCopyWeeks] = useState([]);
  const [copying, setCopying] = useState(false);
  const { goals, results } = wizardData;
  
  // Get goalId and clientId from URL or prop
  const goalId = currentGoalId || searchParams.get('goalId');
  const clientId = searchParams.get('clientId');
  
  // Coach mode: editing a client's plan (not personal)
  const isCoachMode = !!clientId;
  
  // Fetch client info if in coach mode
  useEffect(() => {
    if (clientId) {
      const fetchClientInfo = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/coach/clients/${clientId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setClientName(data.clientName || 'your client');
          }
        } catch (err) {
          console.error('Failed to fetch client info:', err);
        }
      };
      fetchClientInfo();
    }
  }, [clientId]);

  const handleSeeMealPlan = async () => {
    if (isCoachMode) {
      navigate(getClientNutritionPlanExitPath());
      return;
    }
    if (cart.weekItemCount === 0) {
      navigate('/meal-plan');
      return;
    }
    setSyncing(true);
    try {
      await syncWeekPlanToCalendar(cart.weekPlan);
    } catch (error) {
      console.error('Failed to sync to meal plan:', error);
    } finally {
      setSyncing(false);
    }
    navigate('/meal-plan');
  };

  // Handle copying plan to future weeks
  const handleCopyToWeeks = async () => {
    if (copyWeeks.length === 0) return;
    setCopying(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in');
        return;
      }
      // For each selected future week, offset the dates and save
      for (const weekOffset of copyWeeks) {
        const transformedDays = transformWeekPlanForSave(cart.weekPlan).map(day => {
          const original = new Date(day.date);
          const copied = new Date(original);
          copied.setDate(copied.getDate() + weekOffset * 7);
          return { ...day, date: copied.toISOString() };
        });

        const response = await fetch(`${API_URL}/nutrition/goals/${goalId}/weekplan/copy`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            weekPlan: { startDate: transformedDays[0]?.date, days: transformedDays }
          })
        });

        if (!response.ok) {
          // If copy endpoint doesn't exist, fall back to regular weekplan PUT
          await fetch(`${API_URL}/nutrition/goals/${goalId}/weekplan`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              weekPlan: { startDate: transformedDays[0]?.date, days: transformedDays }
            })
          });
        }
      }
      toast.success(`Plan copied to ${copyWeeks.length} additional week${copyWeeks.length > 1 ? 's' : ''}!`);
      setCopyWeeks([]);
    } catch (error) {
      console.error('Copy weeks error:', error);
      toast.error('Failed to copy plan to future weeks');
    } finally {
      setCopying(false);
    }
  };

  const toggleCopyWeek = (weekNum) => {
    setCopyWeeks(prev =>
      prev.includes(weekNum)
        ? prev.filter(w => w !== weekNum)
        : [...prev, weekNum]
    );
  };

  const handleAddToShoppingCart = async () => {
    if (cart.weekItemCount === 0) {
      toast.error('Your meal plan is empty');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to add to your shopping cart');
      return;
    }

    setAddingToCart(true);
    try {
      let addedMenus = 0;
      let addedFoods = 0;

      // Collect unique menu item IDs from the week plan
      const uniqueMenuIds = new Set();
      cart.weekPlan.forEach(day => {
        MEAL_TYPES.forEach(mealType => {
          const meal = day.meals[mealType];
          (meal.menuItems || []).forEach(item => {
            const id = item.menuItem?._id || item.menuItem?.menuItemId;
            if (id) uniqueMenuIds.add(id);
          });
        });
      });

      // Add menu items to cart
      for (const menuId of uniqueMenuIds) {
        try {
          await cartService.addMenuToCart(menuId);
          addedMenus++;
        } catch (err) {
          console.error(`Failed to add menu ${menuId} to cart:`, err);
        }
      }

      // Collect individual foods and aggregate by description
      const foodAggregates = {};
      cart.weekPlan.forEach(day => {
        MEAL_TYPES.forEach(mealType => {
          const meal = day.meals[mealType];
          (meal.foods || []).forEach(item => {
            const food = item.food || {};
            const description = food.description || 'Food item';
            const totalGrams = (item.servingSize || 100) * (item.quantity || 1);
            
            if (foodAggregates[description]) {
              foodAggregates[description].totalGrams += totalGrams;
            } else {
              foodAggregates[description] = {
                description,
                totalGrams,
                brandOwner: food.brandOwner
              };
            }
          });
        });
      });

      // Add aggregated foods as custom items
      for (const food of Object.values(foodAggregates)) {
        try {
          const mass = fromGrams(food.totalGrams, weightUnit);
          const quantity = formatConvertedAmount(mass.quantity, mass.unit);
          const unit = mass.unit;
          const name = food.brandOwner 
            ? `${food.description} (${food.brandOwner})`
            : food.description;
          await cartService.addCustomItem(name, quantity, unit);
          addedFoods++;
        } catch (err) {
          console.error(`Failed to add food "${food.description}" to cart:`, err);
        }
      }

      const totalAdded = addedMenus + addedFoods;
      if (totalAdded > 0) {
        gaEvent({ action: 'add_to_shopping_cart', category: 'ShoppingCart', label: `${totalAdded} items`, value: totalAdded });
        const parts = [];
        if (addedMenus > 0) parts.push(`${addedMenus} recipe${addedMenus > 1 ? 's' : ''}`);
        if (addedFoods > 0) parts.push(`${addedFoods} food${addedFoods > 1 ? 's' : ''}`);
        
        toast.success(
          <span>
            Added {parts.join(' and ')} to your shopping cart!
            <button 
              onClick={() => navigate('/cart')}
              style={{
                marginLeft: '12px',
                padding: '4px 12px',
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              View Cart
            </button>
          </span>,
          { duration: 5000 }
        );
      } else {
        toast.error('No items were added to your shopping cart');
      }
    } catch (error) {
      console.error('Add to shopping cart error:', error);
      toast.error(error.message || 'Failed to add to shopping cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const parseNutrition = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
  };

  // Count menu items vs foods across the week
  const weekStats = cart.weekPlan?.reduce((acc, day) => {
    MEAL_TYPES.forEach(mealType => {
      acc.menuItems += day.meals[mealType].menuItems.length;
      acc.foods += day.meals[mealType].foods.length;
    });
    return acc;
  }, { menuItems: 0, foods: 0 }) || { menuItems: 0, foods: 0 };

  return (
    <div className="step4-review">
      <div className="step-intro">
        <h2><i className="fas fa-clipboard-check"></i> Review Your Week Plan</h2>
        <p>Review your nutrition plan — your progress is auto-saved as you go.</p>
      </div>

      <div className="review-layout">
        {/* Plan Summary */}
        <div className="summary-section">
          <h3><i className="fas fa-chart-pie"></i> Your Nutrition Targets</h3>
          
          <div className="summary-card">
            <div className="summary-header">
              <span className="goal-badge">
                {goals.goalType === 'bulk' && <><i className="fas fa-dumbbell"></i> Bulk</>}
                {goals.goalType === 'maintain' && <><i className="fas fa-balance-scale"></i> Maintain</>}
                {goals.goalType === 'cut' && <><i className="fas fa-fire"></i> Cut</>}
              </span>
              <span className="meals-badge">{goals.mealsPerDay} meals/day</span>
            </div>

            <div className="targets-grid">
              <div className="target-item">
                <span className="target-label">Daily Calories</span>
                <span className="target-value calories">{results?.dailyTargets?.dailyCalories || '-'}</span>
              </div>
              <div className="target-item">
                <span className="target-label">Daily Protein</span>
                <span className="target-value protein">{results?.dailyTargets?.dailyProtein || '-'}g</span>
              </div>
              <div className="target-item">
                <span className="target-label">Daily Carbs</span>
                <span className="target-value carbs">{results?.dailyTargets?.dailyCarbs || '-'}g</span>
              </div>
              <div className="target-item">
                <span className="target-label">Daily Fat</span>
                <span className="target-value fat">{results?.dailyTargets?.dailyFat || '-'}g</span>
              </div>
            </div>
          </div>
        </div>

        {/* Week Summary */}
        <div className="cart-summary-section">
          <h3><i className="fas fa-calendar-week"></i> Week Summary ({cart.weekItemCount} items)</h3>
          
          <div className="cart-summary-card">
            {/* Week Totals */}
            <div className="cart-totals">
              <h4>Week Nutrition Totals</h4>
              <div className="totals-grid">
                <div className="total-item">
                  <span className="total-value">{cart.getWeekTotals?.calories || 0}</span>
                  <span className="total-label">Total Calories</span>
                </div>
                <div className="total-item">
                  <span className="total-value">{cart.getWeekTotals?.protein || 0}g</span>
                  <span className="total-label">Total Protein</span>
                </div>
                <div className="total-item">
                  <span className="total-value">{cart.getWeekTotals?.carbs || 0}g</span>
                  <span className="total-label">Total Carbs</span>
                </div>
                <div className="total-item">
                  <span className="total-value">{cart.getWeekTotals?.fat || 0}g</span>
                  <span className="total-label">Total Fat</span>
                </div>
              </div>
            </div>

            {/* Day-by-Day Breakdown */}
            <div className="meal-breakdown">
              <h4>Day-by-Day Breakdown</h4>
              {cart.getWeekSummary?.map(day => {
                if (!day.hasItems) return null;
                
                return (
                  <div key={day.dayNumber} className="meal-summary-item day-summary">
                    <div className="meal-summary-header">
                      <span className="meal-name">
                        <i className="fas fa-calendar-day"></i>
                        {day.dayName}
                      </span>
                      <span className="meal-stats">
                        {day.totals.calories} cal | {day.totals.protein}g P | {day.itemCount} items
                      </span>
                    </div>
                  </div>
                );
              })}
              
              {cart.getDaysWithItems === 0 && (
                <p className="empty-message">No meals planned yet</p>
              )}
            </div>

            {/* Item Type Summary */}
            <div className="item-type-summary">
              <div className="item-type recipes">
                <i className="fas fa-book-open"></i>
                <span className="count">{weekStats.menuItems}</span>
                <span className="label">Recipes</span>
              </div>
              <div className="item-type foods">
                <i className="fas fa-leaf"></i>
                <span className="count">{weekStats.foods}</span>
                <span className="label">Individual Foods</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-section">
        {/* Add to Shopping Cart Card */}
        <div className="action-card shopping-list-card">
          <div className="action-info">
            <i className="fas fa-cart-plus"></i>
            <div>
              <h4>Add to Shopping Cart</h4>
              <p>Add all recipes and foods to your shopping cart for review</p>
              {(weekStats.menuItems > 0 || weekStats.foods > 0) && (
                <p className="note">{weekStats.menuItems} recipes, {weekStats.foods} individual foods</p>
              )}
            </div>
          </div>
          
          <button 
            className="action-btn shopping-list-btn"
            onClick={handleAddToShoppingCart}
            disabled={addingToCart || cart.weekItemCount === 0}
          >
            {addingToCart ? (
              <><i className="fas fa-spinner fa-spin"></i> Adding...</>
            ) : (
              <><i className="fas fa-cart-plus"></i> Add to Shopping Cart</>
            )}
          </button>
        </div>

        {/* Copy to Future Weeks Card */}
        {!isCoachMode && cart.weekItemCount > 0 && (
          <div className="action-card copy-weeks-card">
            <div className="action-info">
              <i className="fas fa-copy"></i>
              <div>
                <h4>Copy Plan to Future Weeks</h4>
                <p>Repeat this week's meal plan for upcoming weeks</p>
              </div>
            </div>
            <div className="copy-weeks-options">
              {[1, 2, 3].map(weekNum => (
                <label key={weekNum} className="copy-week-option">
                  <input
                    type="checkbox"
                    checked={copyWeeks.includes(weekNum)}
                    onChange={() => toggleCopyWeek(weekNum)}
                  />
                  <span>Week {weekNum} ahead</span>
                </label>
              ))}
            </div>
            {copyWeeks.length > 0 && (
              <button
                className="action-btn copy-weeks-btn"
                onClick={handleCopyToWeeks}
                disabled={copying}
              >
                {copying ? (
                  <><i className="fas fa-spinner fa-spin"></i> Copying...</>
                ) : (
                  <><i className="fas fa-copy"></i> Copy to {copyWeeks.length} Week{copyWeeks.length > 1 ? 's' : ''}</>
                )}
              </button>
            )}
          </div>
        )}

        {/* Back to Dashboard - only in coach mode */}
        {isCoachMode && (
          <div className="action-card dashboard-card">
            <div className="action-info">
              <i className="fas fa-arrow-left"></i>
              <div>
                <h4>Return to Dashboard</h4>
                <p>Leave the planner and return to your dashboard to manage clients</p>
              </div>
            </div>
            
            <button 
              className="action-btn dashboard-btn"
              onClick={() => navigate(getClientNutritionPlanExitPath())}
            >
              <i className="fas fa-arrow-left"></i> Back to Dashboard
            </button>
          </div>
        )}
      </div>

      {/* Completion + Done */}
      <div className="completion-message">
        <i className="fas fa-check-circle"></i>
        <div>
          <h4>Your week plan is ready!</h4>
          <p>
            You've planned {cart.getDaysWithItems} days with {cart.weekItemCount} items 
            totaling {cart.getWeekTotals?.calories || 0} calories 
            and {cart.getWeekTotals?.protein || 0}g of protein.
            Your plan is auto-saved.
          </p>
        </div>
      </div>

      <div className="step4-done-row">
        <button
          className="step4-see-meal-plan-btn"
          onClick={handleSeeMealPlan}
          disabled={syncing && !isCoachMode}
        >
          {syncing && !isCoachMode ? (
            <><i className="fas fa-spinner fa-spin"></i> Syncing...</>
          ) : isCoachMode ? (
            <><i className="fas fa-check"></i> Finish & return to dashboard</>
          ) : (
            <><i className="fas fa-calendar-alt"></i> See Meal Plan</>
          )}
        </button>
      </div>
    </div>
  );
};

export default Step4Review;
